import { revisionCommitted } from './root-reducer'
import { sharedHydrated } from './shared-actions'
import { createEnvelope, readEnvelope, type SharedData, writeEnvelope } from './shared-state'
import { selectSharedData } from './selectors'
import type { AppDispatch, AppThunk } from './create-store'
import type { RootState } from './root-reducer'
import { broadcastRevision } from './sync'
import { withWriteLock } from './sync-context'

export type Outcome<T = void> = { ok: true; value: T } | { ok: false; error: string }

export const ok = <T>(value: T): Outcome<T> => ({ ok: true, value })
export const succeed: Outcome = { ok: true, value: undefined }
export const fail = (error: string): Outcome<never> => ({ ok: false, error })

interface MutationApi {
  dispatch: AppDispatch
  getState: () => RootState
}

/**
 * The only way shared data changes. Inside the write lock it:
 *   1. loads the latest stored state into Redux (so we build on other tabs' changes, never over them),
 *   2. runs `mutate`, which checks rules against that fresh state and dispatches actions,
 *   3. if the mutation succeeded and changed something, persists revision + 1 and tells the other tabs.
 * `mutate` returns an `Outcome`; a failed outcome writes nothing. Nothing outside this function writes to storage,
 * and receiving a sync event only hydrates Redux, so a sync can never trigger another write.
 */
export function commit<T = void>(mutate: (api: MutationApi) => Outcome<T>): AppThunk<Promise<Outcome<T>>> {
  return (dispatch, getState, ctx) =>
    withWriteLock(ctx, (): Outcome<T> => {
      const latest = readEnvelope(ctx.local)
      if (latest && latest.revision > getState().sync.revision) {
        dispatch(sharedHydrated({ data: latest.data, revision: latest.revision }))
      }

      const before = selectSharedData(getState())
      let result: Outcome<T>
      try {
        result = mutate({ dispatch, getState })
      } catch (error) {
        result = fail(error instanceof Error ? error.message : 'Something went wrong.')
      }

      const after = selectSharedData(getState())
      if (!result.ok) {
        // A failed mutation must not leave half-applied state behind.
        if (after !== before) dispatch(sharedHydrated({ data: before, revision: getState().sync.revision }))
        return result
      }
      if (after === before) return result

      const revision = Math.max(latest?.revision ?? 0, getState().sync.revision) + 1
      try {
        writeEnvelope(ctx.local, createEnvelope(after as SharedData, revision, ctx.tabId))
      } catch {
        dispatch(sharedHydrated({ data: before, revision: getState().sync.revision }))
        return fail('Could not save your changes: browser storage is full or unavailable.')
      }
      dispatch(revisionCommitted(revision))
      broadcastRevision(ctx, revision)
      return result
    })
}
